import { useEffect, useRef, type RefObject } from 'react';
import { throttle } from 'lodash';

type ScrollCallback = (event: Event) => void;

type ScrollTarget = HTMLElement | Window | null;

interface UseScrollCallbackOptions {
    /** Element (or ref to it) to listen scroll events on. Defaults to `window`. */
    element?: ScrollTarget | RefObject<HTMLElement | null>;
    /** Throttle wait in ms. */
    wait?: number;
    /** Invoke on the leading edge of the timeout. */
    leading?: boolean;
    /** Invoke on the trailing edge of the timeout. */
    trailing?: boolean;
}

export const useScrollCallback = (
    callback: ScrollCallback,
    { element, wait = 1000 / 60, leading = true, trailing = true }: UseScrollCallbackOptions = {}
) => {
    const callbackRef = useRef<ScrollCallback>(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const resolved = element && 'current' in element ? element.current : element;
        const target = resolved ?? (typeof window !== 'undefined' ? window : null);
        if (!target) {
            return;
        }

        const throttled = throttle((event: Event) => callbackRef.current(event), wait, { leading, trailing });

        target.addEventListener('scroll', throttled, { passive: true });

        return () => {
            target.removeEventListener('scroll', throttled);
            throttled.cancel();
        };
    }, [element, wait, leading, trailing]);
};
